import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';
import { BrandLogo } from './brand';

/**
 * Header — reproduction fidèle de src/components/Navbar.tsx du repo
 * Loveable (header sticky white, lien actif avec barre teal pleine
 * largeur sous le lien, menu mobile slide-over).
 */

const navigation = [
  { name: 'Accueil', href: '/' },
  { name: 'Le Projet', href: '/le-projet' },
  { name: 'Nos Offres', href: '/offres' },
  { name: 'Services', href: '/services' },
  { name: 'Contact', href: '/contact' },
];

export function SiteHeader() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, signOut } = useAuth();
  const targetForLogged =
    user?.role === 'admin' || user?.role === 'super_admin'
      ? '/admin'
      : user?.role === 'technicien'
        ? '/technicien'
        : '/dashboard';

  return (
    <header className="sticky top-0 z-50 w-full backdrop-blur-lg bg-white/90 border-b border-gray-200">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-8"
        aria-label="Global"
      >
        <div className="flex lg:flex-1">
          <Link to="/" className="-m-1.5 p-1.5">
            <BrandLogo />
          </Link>
        </div>

        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Ouvrir le menu"
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="hidden lg:flex lg:gap-x-8">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'text-sm font-medium relative py-2 transition-all hover:text-bcl-blue',
                location.pathname === item.href
                  ? 'text-bcl-blue after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-bcl-teal'
                  : 'text-gray-900',
              )}
            >
              {item.name}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-4">
          {user ? (
            <>
              <Button asChild className="text-sm font-medium">
                <Link to={targetForLogged}>Mon espace</Link>
              </Button>
              <Button
                variant="ghost"
                className="text-sm font-medium"
                onClick={signOut}
              >
                Se déconnecter
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="text-sm font-medium">
                  Se connecter
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="text-sm font-medium">S'inscrire</Button>
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-white lg:hidden">
          <div className="fixed inset-0 flex">
            <div className="relative flex w-full flex-1 flex-col overflow-y-auto bg-white px-4 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
              <div className="flex items-center justify-between">
                <Link to="/" className="-m-1.5 p-1.5">
                  <BrandLogo />
                </Link>
                <button
                  type="button"
                  className="-m-2.5 rounded-md p-2.5 text-gray-700"
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-label="Fermer le menu"
                >
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="mt-6 flow-root">
                <div className="-my-6 divide-y divide-gray-500/10">
                  <div className="space-y-2 py-6">
                    {navigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={cn(
                          '-mx-3 block rounded-lg px-3 py-4 text-base font-medium',
                          location.pathname === item.href
                            ? 'text-bcl-blue bg-bcl-grey'
                            : 'text-gray-900 hover:bg-gray-50',
                        )}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                  <div className="py-6 flex flex-col gap-2">
                    {user ? (
                      <>
                        <Link
                          to={targetForLogged}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <Button className="w-full">Mon espace</Button>
                        </Link>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            signOut();
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          Se déconnecter
                        </Button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/login"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <Button variant="outline" className="w-full">
                            Se connecter
                          </Button>
                        </Link>
                        <Link
                          to="/signup"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <Button className="w-full">S'inscrire</Button>
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
